import React, { useState, useContext, useEffect, FormEvent } from "react";
import { Modal, Form, Button, Row } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useErrors } from "services/customHooks/useErrors";
import TextFormInput from "components/framework/forms/TextFormInput";
import { nameOf } from "services/util/ObjectUtil";
import DropdownFormInput from "components/framework/forms/DropdownFormInput";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { AddError, Errors } from "components/framework/errorHandling/ErrorUtil";
import CloseButton from "components/framework/modals/CloseButton";
import { Billing, getDefaultBilling} from "components/manage/types/Billing";
import { AppContext } from "services/appContext/AppContext";
import { LookupType } from "components/manage/types/LookupType";
import { OverageType } from "components/manage/types/OverageType";
import { BillingApi } from "services/apis/BillingApi";
import { handleError } from "services/util/ApiUtil";
import { showInfoNotification } from "components/framework/notification/NotificationUtil";

type Props = {                 
  closeModal: () => void;
  companyId: string;
  feeTierId?: string;
  onSuccess?: () => void;
};

export default function AddOrUpdateExploreFees(props: Props) {
  const { setErrors, getErrorHandler } = useErrors();
  const [billing, setBilling] = useState<Billing>(getDefaultBilling());
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const intl = useIntl();
  const isMounted = useIsMounted();
  const { appContext } = useContext(AppContext);
  const [range, setRange] = useState({
    startRange: 0,
    endRange: 0,
    overagePrice: 0
  });

  const theme = appContext.theme;

  const addOverageRange = () => {
  setBilling((prev) => ({
    ...prev,
    overageRanges: [...prev.overageRanges, range]
  }));

  setRange({
    startRange: 0,
    endRange: 0,
    overagePrice: 0
  });

  // Clear all overageRange errors when adding a new row
  setErrors((prev) => {
    const cleared = { ...prev };
    Object.keys(cleared).forEach((key) => {
      if (key.startsWith("overageRanges")) delete cleared[key];
    });
    return cleared;
  });
};

const removeRange = (index: number) => {
  setBilling((prev) => ({
    ...prev,
    overageRanges: prev.overageRanges.filter((_, i) => i !== index)
  }));

  // Clear all overageRange errors when removing a row
  setErrors((prev) => {
    const cleared = { ...prev };
    Object.keys(cleared).forEach((key) => {
      if (key.startsWith("overageRanges")) delete cleared[key];
    });
    return cleared;
  });
};

  useEffect(() => {
     if (!props.feeTierId) {
       setBilling(getDefaultBilling()); 
     }
   }, [props.feeTierId]);

  useEffect(() => {
    if (props.feeTierId) {
      setShowLoadingIndicator(true);

      BillingApi.getById(props.companyId, props.feeTierId)
        .then((result) => {
          if (isMounted.current) {
            setBilling({
              id: result.productFeeTierId,
              orderType: result.orderType,
              lookupType: result.lookupType,
              includedQuantity: result.includedQuantity,
              unitPrice: result.unitPrice,
              cap: result.cap,
              overagePrice: result.overagePrice,
              overageType: result.overageType,
              overageRanges: (result.overagePrices || []).map((p: any) => ({
                startRange: p.startRange,
                endRange: p.endRange,
                overagePrice: p.overagePrice
              }))
            });
          }
        })
        .catch((error) => handleError(error))
        .finally(() => {
          if (isMounted.current) {
            setShowLoadingIndicator(false);
          }
        });
    }
  }, [props.companyId, props.feeTierId, isMounted]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const errors = validateBilling(billing);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setShowLoadingIndicator(true);

    let payload: Billing;

    if (billing.overageType === OverageType.Range) {
      const formattedRanges = billing.overageRanges.map((range, index, arr) => ({
      startRange: index === 0 ? 0 : Number(arr[index - 1].endRange) + 1,
      endRange: Number(range.endRange),
      overagePrice: Number(range.overagePrice)
    }));

      payload = {
        ...billing,
        overageRanges: formattedRanges
      };
    } else {
      payload = {
        ...billing,
        overageRanges: []
      };
    }

    if (!props.feeTierId) {
      BillingApi.create(props.companyId, payload)
        .then(() => {
          showInfoNotification(
            intl.formatMessage({ id: "manage.billing.create.successNotificationMessage" })
          );
          props.onSuccess?.();
          props.closeModal();
        })
        .catch((error) => handleError(error))
        .finally(() => setShowLoadingIndicator(false));
    } else {
      BillingApi.update(props.companyId, props.feeTierId, payload)
        .then(() => {
          showInfoNotification(
            intl.formatMessage({ id: "manage.billing.edit.successNotificationMessage" })
          );
          props.onSuccess?.();
          props.closeModal();
        })
        .catch((error) => handleError(error))
        .finally(() => setShowLoadingIndicator(false));
    }
  };
  return (
    <>
      <Modal className="modal-dialog-centered modal-lg" isOpen={true}>
        <div className="modal-header">
          <h5 className="modal-title">
            {!props.feeTierId && <FormattedMessage id="manage.billing.create.title" />}
            {props.feeTierId && <FormattedMessage id="manage.billing.edit.title" />}
          </h5>
          <CloseButton close={props.closeModal} />
        </div>
        <Form onSubmit={(e) => handleSubmit(e)}>
          <div className="modal-body">
            <Row>
              <DropdownFormInput
                className="col-lg-6"
                labelTranslationId="manage.billing.create.lookuptype"
                required
                value={billing.lookupType}
                placeHolder="Select Lookup Type"
                handleInputChange={(value: string) => setBilling({ ...billing, lookupType: value })}
                errorHandler={getErrorHandler(nameOf<Billing>("lookupType"))}
                options={[
                  { key: "Select Lookup Type", value: "" },
                  { key: "Explore", value: LookupType.Explore },
                  { key: "CNAM", value: LookupType.CNAM },
                  { key: "History", value: LookupType.History }
                ]}
              />
            </Row>
            <Row>
              <TextFormInput
                formGroupClassName="col-lg-6"
                labelTranslationId="manage.billing.create.includedquantity"
                required
                value={billing.includedQuantity}
                placeholderTranslationId="manage.billing.create.includedquantity.placeholder"       
                handleInputChange={(value: string) =>
                  setBilling({
                    ...billing,
                    includedQuantity: Number(value)
                  })
                }
                errorHandler={getErrorHandler(nameOf<Billing>("includedQuantity"))}
              />
            </Row>
            <Row>
              <TextFormInput
                formGroupClassName="col-lg-6"
                labelTranslationId="manage.billing.create.unitprice"
                required
                value={billing.unitPrice}
                placeholderTranslationId="manage.billing.create.unitprice.placeholder"
                handleInputChange={(value: string) => {
                   if (/^\d*\.?\d*$/.test(value)) {
                     setBilling({
                       ...billing,
                       unitPrice: value
                     });
                   }
                 }}
                errorHandler={getErrorHandler(nameOf<Billing>("unitPrice"))}
              />
            </Row>
            <Row>
              <TextFormInput
                formGroupClassName="col-lg-6"
                labelTranslationId="manage.billing.create.cap"
                required
                value={billing.cap}
                placeholderTranslationId="manage.billing.create.cap.placeholder"
                handleInputChange={(value: string) =>
                  setBilling({
                    ...billing,
                    cap: Number(value)
                  })
                }
                errorHandler={getErrorHandler(nameOf<Billing>("cap"))}
              />
            </Row>
            <Row>
              <TextFormInput
                formGroupClassName="col-lg-6"
                labelTranslationId="manage.billing.create.overageprice"
                required
                value={billing.overagePrice}
                placeholderTranslationId="manage.billing.create.overagePrice.placeholder" 
                handleInputChange={(value: string) => {
                   if (/^\d*\.?\d*$/.test(value)) {
                     setBilling({
                       ...billing,
                       overagePrice: value
                     });
                   }
                 }}
                errorHandler={getErrorHandler(nameOf<Billing>("overagePrice"))}
              />
            </Row>
            <Row>
              <DropdownFormInput
                className="col-lg-6"
                labelTranslationId="manage.billing.create.overagetype"
                required
                value={billing.overageType}
                handleInputChange={(value: string) => {
               // Clear range errors on type change
               setErrors((prev) => {
                 const cleared = { ...prev };
                 Object.keys(cleared).forEach((key) => {
                   if (key.startsWith("overageRanges")) delete cleared[key];
                 });
                 return cleared;
               });

               setBilling((prev) => {
                 if (value === OverageType.Range && (!prev.overageRanges || prev.overageRanges.length === 0)) {
                   return {
                     ...prev,
                     overageType: value,
                     overageRanges: [{ startRange: 0, endRange: 0, overagePrice: 0 }]
                   };
                 }
                 if (value === OverageType.Fixed) {
                   return { ...prev, overageType: value, overageRanges: [] };             
                 }
                 return { ...prev, overageType: value };
               });
              }}
                errorHandler={getErrorHandler(nameOf<Billing>("overageType"))}
                options={[
                  { key: "Select Overage Type", value: "" },
                  { key: "Fixed", value: OverageType.Fixed },
                  { key: "Range", value: OverageType.Range }
                ]}
              />
            </Row>
            {billing.overageType === OverageType.Range && (
              <>
                {billing.overageRanges.map((item, index) => (
                  <div className="row mt-2" key={index}>
                    <TextFormInput
                      formGroupClassName="col-lg-4"
                      labelTranslationId="manage.billing.create.endrange"
                      required
                      value={item.endRange}
                      handleInputChange={(value: string) => {
                        const updated = [...billing.overageRanges];
                        updated[index].endRange = Number(value);
                        setBilling({ ...billing, overageRanges: updated });
                      }}
                      errorHandler={getErrorHandler(`overageRanges[${index}].endRange`)}
                    />
                    <TextFormInput
                      formGroupClassName="col-lg-4"
                      labelTranslationId="manage.billing.create.overageprice"
                      required
                      value={item.overagePrice}
                      handleInputChange={(value: string) => {
                       if (/^\d*\.?\d*$/.test(value)) {
                         const updated = [...billing.overageRanges];
                         updated[index].overagePrice = value;
                         setBilling({ ...billing, overageRanges: updated });
                       }
                     }}
                      errorHandler={getErrorHandler(`overageRanges[${index}].overagePrice`)}
                    />

                    <div className="col-lg-2 d-flex align-items-center mt-3">
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeRange(index)}>
                        <i
                          className="fas fa-minus-circle text-danger"
                          style={{ fontSize: "20px" }}
                        />
                      </button>
                    </div>
                  </div>
                ))}
                {billing.overageRanges.length === 0 && (
                  <div className="row mt-2">
                    <div className="col-lg-12">
                      {getErrorHandler("overageRanges").getErrors()?.length > 0 && (
                        <small className="text-danger" style={{ fontSize: "15px" }}>
                          {getErrorHandler("overageRanges").getErrors()[0].message}
                        </small>
                      )}
                    </div>
                  </div>
                )}
                {/* ADD NEW ROW */}
                <div className="row mt-3">
                  <div className="col-lg-2">
                    <button type="button" className="btn btn-link p-0" onClick={addOverageRange}>
                      <i className="fas fa-plus-circle text-success" style={{ fontSize: "22px" }} />
                      <span className="ml-2">Add Range</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <Button
              color="link"
              type="button"
              className={`${theme === "light" ? "lblue-color" : ""}`}
              onClick={() => props.closeModal()}>
              <FormattedMessage id="manage.organization.edit.cancelButton" />
            </Button>
            <Button
              color="primary"
              type="submit"
              className={`ml-auto ${theme === "light" ? "bg-lblue no-border" : ""}`}
              disabled={showLoadingIndicator}>
              {showLoadingIndicator && <i className="fas fa-spinner fa-spin mr-2" />}
              <FormattedMessage id="manage.organization.edit.submitButton" />
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

const validateBilling = (billing: Billing) => {
  const errors: Errors = {};

  if (!billing.lookupType) {
    AddError(errors, nameOf<Billing>("lookupType"), "manage.billing.lookupType.required");
  }

  if (!billing.includedQuantity || billing.includedQuantity <= 0) {
    AddError(
      errors,
      nameOf<Billing>("includedQuantity"),
      "manage.billing.includedQuantity.required"
    );
  }

  if (!billing.unitPrice) {
    AddError(errors, nameOf<Billing>("unitPrice"), "manage.billing.unitPrice.required");
  }

  if (!billing.cap || billing.cap <= 0) {
    AddError(errors, nameOf<Billing>("cap"), "manage.billing.cap.required");
  }

  if (billing.cap && billing.includedQuantity && billing.cap < billing.includedQuantity) {
    AddError(
      errors,
      nameOf<Billing>("cap"),
      "manage.billing.cap.mustBeGreaterThanIncludedQuantity"
    );
  }

  if (!billing.overageType) {
    AddError(errors, nameOf<Billing>("overageType"), "manage.billing.overageType.required");
  }

  if (!billing.overagePrice) {
    AddError(errors, nameOf<Billing>("overagePrice"), "manage.billing.overagePrice.required");
  }

  if (billing.overageType === OverageType.Range) {
    if (!billing.overageRanges || billing.overageRanges.length === 0) {
      AddError(errors, "overageRanges", "Please add at least one overage range.", false);
    }

    billing.overageRanges.forEach((range, index) => {
     const endRangeValue = Number(range.endRange);
     const overagePriceValue = Number(range.overagePrice);

      if (!range.endRange || endRangeValue  <= 0) {
        AddError(
          errors,
          `overageRanges[${index}].endRange`,
          "manage.billing.endRange.required"
        );
      }
      if (!range.overagePrice || overagePriceValue <= 0) {
        AddError(
          errors,
          `overageRanges[${index}].overagePrice`,
          "manage.billing.overagePrice.required"
        );
      }
      if (
         index > 0 &&
         range.endRange &&
         billing.overageRanges[index - 1].endRange &&
         range.endRange <= billing.overageRanges[index - 1].endRange
       ) {
         AddError(
           errors,
           `overageRanges[${index}].endRange`,
           "manage.billing.endRange.mustBeGreater"
         );
       }     

    });
  }
  return errors;
};
